import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import faker from '@faker-js/faker';
import { useMediaQuery } from '@material-ui/core';

import { BackgroundImage, Flex, Text, ProgressBar, SvgIcon, Box } from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';
import { getDuration } from 'Util/datetime';
import CourseExpiredModal from 'Routes/Classroom/Components/CourseExpiredModal';
import { AccessContentModal, MESSAGE_TYPE } from 'Components/AccessContentModal';

import { MODAL_TYPE, BADGE_VARIANTS } from './CourseCard';

const CardContainer = styled((props) => (
  <Flex as={'a'} width={1} alignItems={'start'} {...props} />
))`
  border-bottom: 1px solid ${getTheme('colors.greyscale.grey4')};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  padding: 16px 0px;
`;

const CourseLockedOverlay = styled((props) => (
  <Flex width={1} height="100%" justifyContent={'center'} alignItems={'center'} {...props} />
))`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
`;

const CourseImage = styled(BackgroundImage)`
  padding: 0;
  background-position: top center;
  height: 100%;
`;

const CardDetailsContainer = styled((props) => (
  <Flex pl={3} width={435} flexDirection={'column'} {...props} />
))`
  overflow: hidden;
`;

const CardTitle = styled((props) => <Text color={'text'} mb={1} {...props} />)`
  max-width: 400px;
  height: 44px;
  overflow: hidden;
`;

const Description = styled((props) => <Text textStyle={'caption'} mt={1} {...props} />)`
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;
  line-height: 1.25;
`;

const ImageWrapper = styled(Box)`
  position: relative;
  border-radius: 6px;
  overflow: hidden;

  width: 128px;
  height: 72px;
  min-width: 128px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 190px;
    height: 105px;
    min-width: 190px;
  }
`;

const BadgeContainer = styled((props) => <Flex py={1} px={2} {...props} />)`
  border-radius: 14px;
  position: absolute;
  right: 8px;
  top: 8px;
  ${({ variant }) =>
    variant === BADGE_VARIANTS.EBOOK
      ? css`
          background-color: ${({ theme }) => theme.colors.white};
        `
      : css`
          background-color: ${({ theme }) => theme.colors.warning};
        `}
`;

const BadgeText = (props) => {
  return <Text textStyle={'caption'} color={'black'} fontWeight={'bold'} {...props} />;
};

const Badge = ({ variant, totalActiveLearningModes }) => {
  switch (variant) {
    case BADGE_VARIANTS.EBOOK:
      return (
        <BadgeContainer variant={variant}>
          <BadgeText>E-Book</BadgeText>
        </BadgeContainer>
      );
    case BADGE_VARIANTS.LEARNING_MODE:
      return (
        <BadgeContainer variant={variant}>
          <BadgeText>{`${totalActiveLearningModes} โหมด`}</BadgeText>
        </BadgeContainer>
      );
    default:
      return null;
  }
};

const truncateTextFromMiddle = (text, countTextBefore) => {
  if (text.length > 35) {
    const countNextText = 16;
    const beforeText = text.substring(0, countTextBefore);
    const afterText = text.substring(text.length - countNextText, text.length);
    const ellipsis = '...';

    return beforeText + ellipsis + afterText;
  }

  return text;
};

const CardDetails = ({ title: textTitle, timeLeft, expDate, progress, progressLabel, footer }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(`(min-width:${theme?.breakpoints?.lg ?? '1024px'})`);
  const countTextBefore = isDesktop ? 80 : 30;
  const title = truncateTextFromMiddle(textTitle, countTextBefore);

  return (
    <CardDetailsContainer>
      <CardTitle>{title}</CardTitle>
      <ProgressBar
        pr={7}
        value={progress}
        label={progressLabel}
        textProps={{
          fontWeight: 300,
          fontSize: '12px',
        }}
      />
      <Description color={'secondaryLight'}>
        {expDate ? expDate : null}
        {expDate && timeLeft ? <br /> : null}
        {timeLeft ? timeLeft : null}
        {footer && (
          <Text textStyle={'caption'} as={'span'} ml={2}>
            {footer}
          </Text>
        )}
      </Description>
    </CardDetailsContainer>
  );
};

const CourseCardHorizontalWithExpiry = ({
  imageUrl,
  defaultImageUrl,
  title,
  time,
  isLockedCourse,
  isShowRemainingTime,
  expDate,
  onClick,
  progress,
  footer,
  modalType,
  containerProps,
  name,
  badgeVariant,
  totalActiveLearningModes,
}) => {
  const { t } = useTranslation('products');
  const [isOpenLockedCourseModal, setIsOpenLockedCourseModal] = useState(false);

  const handleOnClick = () => {
    if (isLockedCourse) {
      setIsOpenLockedCourseModal(true);
    } else {
      return onClick;
    }
  };

  const handleCloseModal = () => {
    setIsOpenLockedCourseModal(false);
  };

  return (
    <Fragment>
      <CardContainer
        data-testid={`${name}-open`}
        p={0}
        onClick={isLockedCourse ? handleOnClick : onClick}
        {...containerProps}
      >
        <ImageWrapper>
          <CourseImage src={imageUrl || defaultImageUrl} />

          <Badge variant={badgeVariant} totalActiveLearningModes={totalActiveLearningModes} />

          {isLockedCourse && (
            <CourseLockedOverlay>
              <SvgIcon icon={SvgIcon.ICON_LIST.LOCKED} color={'white'} size={24} />
            </CourseLockedOverlay>
          )}
        </ImageWrapper>

        <CardDetails
          title={title}
          timeLeft={
            isShowRemainingTime ? t('COURSE_CARD.TIME_LEFT', { timeLeft: getDuration(time) }) : null
          }
          progress={progress}
          progressLabel={t('COURSE_CARD.PROGRESS_SUFFIX')}
          footer={footer}
          expDate={
            expDate
              ? t('COURSE_CARD.EXP_DATE', { expDate: dayjs(expDate).add(-1, 'minute') })
              : isShowRemainingTime
              ? t('COURSE_CARD.NO_EXP_DATE')
              : null
          }
        />
      </CardContainer>

      {modalType === MODAL_TYPE.EXPIRY_MODAL && (
        <CourseExpiredModal isActive={isOpenLockedCourseModal} onContinue={handleCloseModal} />
      )}
      {modalType === MODAL_TYPE.REMAINING_TIMEOUT && (
        <AccessContentModal
          messageType={MESSAGE_TYPE.COURSE_REMAINING_TIMEOUT}
          muiProps={{ isActive: isOpenLockedCourseModal }}
          onClick={handleCloseModal}
        />
      )}
    </Fragment>
  );
};

CourseCardHorizontalWithExpiry.propTypes = {
  imageUrl: PropTypes.string,
  defaultImageUrl: PropTypes.string,
  title: PropTypes.string,
  time: PropTypes.number,
  isLockedCourse: PropTypes.bool,
  isShowRemainingTime: PropTypes.bool,
  expDate: PropTypes.string,
  onClick: PropTypes.func,
  progress: PropTypes.number,
  footer: PropTypes.string,
  modalType: PropTypes.string,
  containerProps: PropTypes.object,
  name: PropTypes.string,
  badgeVariant: PropTypes.string,
  totalActiveLearningModes: PropTypes.number,
};

CardDetails.propTypes = {
  title: PropTypes.string,
  timeLeft: PropTypes.string,
  expDate: PropTypes.string,
  progress: PropTypes.number,
  progressLabel: PropTypes.string,
  footer: PropTypes.string,
};

CourseCardHorizontalWithExpiry.defaultProps = {
  onClick: () => {},
  name: faker.random.uuid(),
};

export default CourseCardHorizontalWithExpiry;
